import React, { useState, useMemo, useCallback, createContext, useContext, lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useParams } from 'react-router-dom';
import { CssBaseline, GlobalStyles, useMediaQuery } from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import { MotionConfig } from 'framer-motion';
import { Toaster } from 'sonner';

import buildTheme from './theme/theme';
import { MOBILE_BOTTOM_NAV_CLEARANCE_PX, TOUCH_TARGET_COARSE } from './theme/tokens';
import ErrorBoundary from './components/common/ErrorBoundary';
import RouteFallback from './components/common/RouteFallback';
import PullToRefresh from './components/common/PullToRefresh';
import { lazyMitNeuladen } from './utils/lazyMitNeuladen';
// UI2 Teil 9, Z-1a: der geteilte Erreichbarkeits-Waechter. Banner und Provider
// sitzen AUSSERHALB von <Routes> — damit erscheint der Banner auch auf der
// oeffentlichen Route `/vendor-reports/public/:id` und im Anmeldezustand.
import { ServiceOutageProvider } from './serviceOutage/ServiceOutageProvider';
import ServiceOutageBanner from './components/common/ServiceOutageBanner';

// import { LoadingProvider } from './contexts/LoadingContext';
// import { LoadingOverlay } from './components/LoadingOverlay';
import SidebarAndHeaderBar from './components/SidebarAndHeaderBar';
import POOverview from './POOverview';
import ProtectedRoute from './ProtectedRoute';
// UI2 Teil 10, G0.11 — die Musterroute `/design/muster`. Der Eintrag kommt aus
// einem eigenen Modul, weil er in einem Produktionsbau LEER ist (`null`): der
// `NODE_ENV`-Riegel und `lazy()` stehen dort zusammen (Plan §4.1).
import { musterrouteEintrag } from './design/muster/musterroute';
import DebugLogs from './DebugLogs';
import NewProductWizard from './NewProductWizard';
import UnifiedCreateWizard from './components/UnifiedCreateWizard';
import useMountOnFirstOpen from './hooks/useMountOnFirstOpen';
import IncomingInventory from './IncomingInventory';
// EK-D17 — die gespiegelten K&N-Sendungen, nur lesend.
import KnShipments from './pages/KnShipments';
import KnShipmentDetail from './pages/KnShipmentDetail';
// Strang 1a-6 — die Uebersicht der Produkt-Vollstaendigkeit (nur lesend).
import ProductCompletionOverview from './pages/ProductCompletionOverview';
//import ProductOverview from './ProductOverview'
import FulfillmentDashboard from './components/fulfillment/FulfillmentDashboard';
import ProductsOverview from './ProductsOverview'
import ProductsAttributesPage from './ProductsAttributesPage'
import HomeDashboard from './home/HomeDashboard'
import ScraperConfigForm from './components/ScraperConfigForm';
import CompetitorList from './components/CompetitorList';
import ScraperConfigList from './components/ScraperList';
import FCADashboard from './components/fca/FCADashboard';
import VendorWizard from './components/VendorWizard';
import VendorsOverview from './VendorsOverview';
// Direkt aus der Datei, nicht ueber das Barrel `./components/recommendations`:
// das Barrel reicht auch die ungenutzte Graph-Ansicht weiter, deren CSS-Import
// als Seiteneffekt zaehlt, und hielt so @xyflow/react und dagre (rund 200 KB
// toter Code) im Hauptbuendel (gemessen 2026-09-30, siehe fix/web-build-main-
// bundle-unter-5mb).
import RecommendationsPage from './components/recommendations/RecommendationsPage';
import TaxonomyHub from './pages/TaxonomyHub';
import SourceManagerPage from './components/SourceManagerPage';
import { RMAWizardProvider } from './rma/RMAWizardContext';
import RMAReports from './rma/RMAReports';
import RMASlaOverview from './rma/RMASlaOverview';
import RMAFaelligkeit from './rma/RMAFaelligkeit'; import Monatsabschluss from './pages/Monatsabschluss'; import CustomerDetail from './pages/CustomerDetail';
// Meldungsregister Stufe 1 (Aufgabe 9): die Lese-Flaeche — Liste und Detail.
import Meldungen from './pages/Meldungen';
import MeldungDetail from './pages/MeldungDetail';
import ProductInquiryAnalytics from './rma/ProductInquiryAnalytics';
import InquiryKnowledgeAdmin from './rma/InquiryKnowledgeAdmin';
// S5.4-F — die Kundenrechnungen (Forge-Ausgangsrechnungen), nur lesend.
import InvoiceList from './pages/invoicing/InvoiceList';
import InvoiceDetail from './pages/invoicing/InvoiceDetail';
import POInbox from './POInbox';
import DiscoveryInbox from './pages/DiscoveryInbox'
import RedirectReviewInbox from './pages/RedirectReviewInbox'
import SecondLifeBestand from './pages/SecondLifeBestand'
import WatchFindings from './pages/WatchFindings'
import DepHealth from './ops/DepHealth';
import LsmWatcher from './ops/LsmWatcher';
import PoAnomalies from './ops/PoAnomalies';
import NotFound from './components/NotFound';
import DocumentTitle from './components/DocumentTitle';
import { NotificationsProvider } from './notifications/NotificationsContext'
import AuthGate from './AuthGate'
import NotificationCenter from './components/notifications/NotificationCenter'
import SettingsNotifications from './pages/SettingsNotifications'
import EmailLog from './admin/EmailLog'
import GDPRWipePanel from './admin/GDPRWipePanel'
import PrivacyErasePanel from './admin/PrivacyErasePanel'
import NotificationTester from './admin/NotificationTester'
import AdminLayout from './admin/v2/AdminLayout'
import AdminDashboard from './admin/v2/AdminDashboard'
import IntegrationsList from './admin/v2/IntegrationsList'
import JobsList from './admin/v2/JobsList'
import JobDetail from './admin/v2/JobDetail'
import AuditFeed from './admin/v2/AuditFeed'
import SecretsList from './admin/v2/SecretsList'
import DocsIndex from './admin/v2/DocsIndex'
import DocViewer from './admin/v2/DocViewer'
import AdminSettings from './admin/v2/AdminSettings'
import NotificationTriggers from './admin/v2/NotificationTriggers'
import CronFailures from './admin/v2/CronFailures'
import SparePartReviewQueue from './admin/SparePartReviewQueue'

// 🔴 Die schweren Routen-Seiten werden LAZY geladen. Gemessen am 2026-09-21:
// die Anwendung lag 44.484 Bytes unter CRAs fest verdrahteter Precache-Grenze
// (5 MiB), und ein Ueberschreiten bricht den Bau unter CI=true — also jeden
// Render-Deploy. Diese 15 Seiten sind razem ~1,1 MB Quelltext und werden beim
// ersten Aufruf nicht gebraucht.
// ⚠️ `UnifiedCreateWizard` bleibt ABSICHTLICH eager: er wird ausserhalb des
// <Routes>-Blocks gerendert (unten, nach `</Routes>`) und stuende sonst hinter keinem
// Suspense. 🔴 Der IMPORT bleibt eager — die MONTAGE ist seit Welle 4 aufgeschoben
// (`useMountOnFirstOpen`): beide Wizards haengen erst ab ihrem ersten Oeffnen im Baum,
// also nicht mehr auf jeder Route.
// 🔴 Jede dieser Routen laeuft durch `lazyMitNeuladen`: ein ueber ein Deployment
// offen gebliebener Tab verlangt sonst einen Chunk-Hash, den der neue Bau nicht
// mehr kennt, und landet auf der Fehlerkarte, obwohl ein Neuladen heilt. Die zwei
// `lazy` TEILBAEUME (`ProductDetail.jsx`, `design/DesignCatalog.jsx`) bleiben
// bewusst draussen — dort ist der Weg heraus oertlich, nicht das Neuladen.
const PODetail = lazy(lazyMitNeuladen(() => import('./PODetail')));
const ProductDetail = lazy(lazyMitNeuladen(() => import('./ProductDetail')));
const CompetitorDetail = lazy(lazyMitNeuladen(() => import('./CompetitorDetail')));
const VendorDetails = lazy(lazyMitNeuladen(() => import('./components/VendorDetails')));
const ProductTaxonomyDetail = lazy(lazyMitNeuladen(() => import('./ProductTaxonomyDetail')));
const MarketLab = lazy(lazyMitNeuladen(() => import('./pages/MarketLab')));
const RMAOverviewLayout = lazy(lazyMitNeuladen(() => import('./rma/RMAOverviewLayout')));
const RMADetail = lazy(lazyMitNeuladen(() => import('./rma/RMADetail')));
const RMANewCaseWizard = lazy(lazyMitNeuladen(() => import('./rma/RMANewCaseWizard')));
// Neue Fallansicht (Schnitt N1). Eigener Ordner `fall/`.
// 🔴 **Seit W4 (2026-10-23) ist die ALTE Ansicht wieder der Standard.** Der
// Owner, woertlich: *"Ja, Classic als standard wieder setzen - und pruefen, warum
// der Standard view mobil in crashed!"* Also liegt `rma/:id` wieder auf
// `rma/RMADetail`, und die neue Ansicht zieht auf `rma/:id/neu`. Das dreht die
// Umschaltung S1-8f (Owner-Entscheid 42) zurueck, bis der Absturz am Telefon
// gefunden und behoben ist; die neue Ansicht bleibt unter ihrer Adresse und
// ueber den Kopfmenue-Eintrag „Open new view (preview)" erreichbar.
const FallAnsicht = lazy(lazyMitNeuladen(() => import('./fall/FallAnsicht')));
const RMAInbox = lazy(lazyMitNeuladen(() => import('./rma/RMAInbox')));
const VendorReports = lazy(lazyMitNeuladen(() => import('./pages/VendorReports')));
const VendorReportView = lazy(lazyMitNeuladen(() => import('./pages/VendorReportView')));
const RefundsReport = lazy(lazyMitNeuladen(() => import('./pages/RefundsReport')));
const MediaLibraryPage = lazy(lazyMitNeuladen(() => import('./pages/MediaLibrary')));
// Der Komponentenkatalog ist eine Werkzeugflaeche, keine Produktoberflaeche —
// er gehoert nicht in das Bundle, das jeder beim ersten Aufruf laedt. Er haengt
// als Kind von `path="/"` unter dem geteilten Suspense in `SidebarAndHeaderBar`
// und bringt deshalb KEINE eigene Ladegrenze mit.
const DesignCatalog = lazy(lazyMitNeuladen(() => import('./design/DesignCatalog')));
// ComingSoon retired — every admin section now ships a real page.

// ── Color mode context ─────────────────────────────────────────
const ColorModeContext = createContext({ mode: 'light', toggleColorMode: () => {} });
export const useColorMode = () => useContext(ColorModeContext);

/**
 * The Kanban surface at `/rma/board` was deleted on 2026-08-13 (Owner-Entscheid
 * Frage 32). This keeps the old, bookmarkable URL working: without it the
 * dynamic `rma/:id` route matches and RMADetail tries to load a case named
 * "board".
 *
 * It carries the query string across on purpose. `/rma/board?q=schmidt` was a
 * shareable link, and the whole toolbar state (`filter`, `status`, `case_type`,
 * `q`) is URL-driven and identical on `/rma` — dropping it would silently widen
 * the operator's result set at the moment the redirect fires.
 *
 * Declaration ORDER is irrelevant here: react-router 7 ranks routes by
 * specificity (a static segment outscores a dynamic one), so this wins over
 * `rma/:id` wherever it sits. Do not add an ordering comment claiming otherwise.
 */
function BoardRedirect() {
  const location = useLocation();
  return <Navigate to={{ pathname: '/rma', search: location.search }} replace />;
}

function RelationsGraphRedirect() {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  if (!params.get('view')) {
    params.set('view', 'graph');
  }
  const query = params.toString();
  const target = query ? `/recommendations?${query}` : '/recommendations';
  return <Navigate to={target} replace />;
}

/** `/competitors/:compId/edit` ist im UX-Land-Rueckbau (PR 1) entfallen. Die alte
 *  Zeile renderte bereits `CompetitorDetail` — die Bearbeitung sitzt jetzt IN der
 *  Detailseite. Ein Lesezeichen fuehrt daher auf `competitors/:compId`, nicht ins
 *  Leere. `compId` wird aus dem Pfad uebernommen, damit die Auswahl erhalten bleibt.
 *  🔴 NICHT `to=".."`: relative Ziele loesen ueber die ROUTEN-Hierarchie auf, und
 *  `competitors/:compId/edit` ist eine flache Route — `..` traefe `/`, nicht die
 *  Detailseite. Der Parameter wird darum ausgeschrieben.
 *  Exportiert fuer `App.redirects.test.jsx` (Rotprobe der Parameteruebernahme). */
export function CompetitorEditRedirect() {
  const { compId } = useParams();
  return <Navigate to={`/competitors/${compId}`} replace />;
}

/** Der Altpfad `meldungen/:id` heisst seit 2026-10-19 `/declarations/:id`. Ein Lesezeichen fuehrt weiter,
 *  und die `id` kommt mit: ein nacktes `/declarations` landete auf der Liste statt auf der
 *  gemeinten Meldung. Exportiert fuer `App.redirects.test.jsx` (Rotprobe der Parameteruebernahme). */
export function DeclarationDetailRedirect() {
  const { id } = useParams();
  return <Navigate to={`/declarations/${id}`} replace />;
}


function App() {
  const [wizardOpen, setWizardOpen] = useState(false);
  const openWizard = () => setWizardOpen(true);
  const closeWizard = () => setWizardOpen(false);
  const [variantWizardOpen, setVariantWizardOpen] = useState(false);
  const openVariantWizard = () => setVariantWizardOpen(true);
  const closeVariantWizard = () => setVariantWizardOpen(false);

  // 🔴 Welle 4 / Karte 5 — die zwei Wizards erst ab dem ersten Oeffnen montieren.
  // Ein `{open && …}` waere falsch: es schnitte die Schliess-Animation ab (Grill
  // W3). `useMountOnFirstOpen` traegt den Zustand JE AUFRUFSTELLE — ein
  // gemeinsamer Wert wuerde beim Oeffnen des Varianten-Wizards auch den
  // Produkt-Wizard montieren (die zwei sind Instanzen DESSELBEN Bauteils).
  // Es liefert `{ mounted, open }`: `open` ist beim ERSTEN Oeffnen einen Render
  // lang `false`, damit das Blatt geschlossen montiert und dann einblenden kann
  // (MUI `appear` — Auflage C1 der Runde 1).
  const productWizard = useMountOnFirstOpen(wizardOpen);
  const variantWizard = useMountOnFirstOpen(variantWizardOpen);

  // Respect prefers-reduced-motion for all framer-motion animations
  const prefersReducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)');

  // Dark mode: manual override stored in localStorage, system preference as fallback
  const systemPrefersDark = useMediaQuery('(prefers-color-scheme: dark)');
  const [manualMode, setManualMode] = useState(() => {
    try { return localStorage.getItem('pog-color-mode'); } catch { return null; }
  });
  const effectiveMode = manualMode || (systemPrefersDark ? 'dark' : 'light');
  const muiTheme = useMemo(() => buildTheme(effectiveMode), [effectiveMode]);
  const toggleColorMode = useCallback(() => {
    const next = effectiveMode === 'dark' ? 'light' : 'dark';
    setManualMode(next);
    try { localStorage.setItem('pog-color-mode', next); } catch { /* quota */ }
  }, [effectiveMode]);
  const colorModeCtx = useMemo(
    () => ({ mode: effectiveMode, toggleColorMode }),
    [effectiveMode, toggleColorMode],
  );

  return (
    <MotionConfig reducedMotion={prefersReducedMotion ? 'always' : 'never'}>
    <ColorModeContext.Provider value={colorModeCtx}>
    <ThemeProvider theme={muiTheme}>
      <CssBaseline />
      {/* sonner Toaster — single mount, used app-wide via toast.success/error/etc.
          Replaces the per-page MUI Snackbar pattern. richColors gives semantic
          tinted backgrounds; closeButton adds an explicit X. Mobile gets
          swipe-down-to-dismiss out of the box. Position is offset upward on
          mobile so toasts clear the floating MobileBottomNav. */}
      {/* 🔴 Der Aktionsknopf JEDES Toasts traegt den Haus-Boden auf BEIDEN
          Achsen der Haus-Regel (`pattern-library.md:1084`: „≥ 44 px auf grobem
          Zeiger und unter `sm`") — grober Zeiger ueber `TOUCH_TARGET_COARSE`
          (44 px, Regel 22 §8, dasselbe Token wie `MuiButton`/`MuiIconButton` in
          `theme.js`), unter `sm` ueber `theme.breakpoints.down('sm')` — dieselbe
          Breakpoint-Quelle wie `MuiButton.root` (`theme.js:559-560`), keine
          handgeschriebene Media-Query. Die Stelle ist der Toaster, weil es genau
          EINEN gibt: die Ruecknahmen aus K-F3 (`rma/RMADetail`), Teil 5 M2
          (`notifications/NotificationCenter`) und
          `components/products/SparePartsSection` haengen alle an ihm.

          Warum der Selektor so aussieht: sonner zeichnet den Knopf selbst
          (`<button data-button data-action>`) und setzt in seiner eigenen CSS
          `[data-sonner-toast][data-styled=true] [data-button]{height:24px}`.
          `min-height` schlaegt `height` im Box-Modell unabhaengig von der
          Spezifitaet, deshalb braucht der Boden hier weder eine zweite Zahl noch
          ein `!important`. Gemessen 2026-10-09 im Browser: 24 px auf feinem
          Zeiger ab `sm` (1280 px), >= 44 px auf grobem (1024 px) und auf feinem
          unter `sm` (390 px). 🔴 Der Schliessen-Knopf (`[data-close-button]`,
          absolut positioniert) bleibt unberuehrt — er ist kein Aktionsknopf und
          traegt `data-button` NICHT. */}
      <GlobalStyles
        styles={(theme) => ({
          '[data-sonner-toast] [data-button]': {
            [theme.breakpoints.down('sm')]: { minHeight: 44 },
            ...TOUCH_TARGET_COARSE,
          },
        })}
      />
      <Toaster
        richColors
        closeButton
        position="bottom-center"
        // Sonner nimmt hier eine ZAHL (kein CSS), deshalb die `_PX`-Fassung
        // derselben Konstante, die `theme.js` und die Tabellenfuesse benutzen.
        offset={{ mobile: MOBILE_BOTTOM_NAV_CLEARANCE_PX }}
        toastOptions={{
          // Nur die SCHRIFTGROESSE des Toasts. Hier stand bis zum 2026-10-09
          // „Slightly larger touch target than sonner's default for mobile." —
          // das war gemessen falsch: eine `fontSize` ist keine Hoehe. Die Hoehe
          // des Aktionsknopfes setzt die `GlobalStyles`-Zeile darueber.
          style: { fontSize: '0.875rem' },
        }}
      />
      {/* <LoadingProvider> */}
        {/*<LoadingOverlay messages={funnyMessages} />*/}
        <Router basename={process.env.PUBLIC_URL || '/'}>
          <DebugLogs />
          <DocumentTitle />
          {/* UI2 Teil 9, Z-1a (E-10): der Ausfall-Banner steht fest ueber der
              Schale — ausserhalb <Routes> und damit ausserhalb von `main`. Das
              ist noetig: die E-3-Theme-Regel (`body.pog-ausfall main &`) blendet
              Fehler im Seiteninhalt aus; laege der Banner IN `main`, faenge er
              sich selbst. Der Provider feuert `pog:service-online` beim Uebergang
              `ausfall -> online` (der Nachzug Z-1c haengt sich daran). */}
          <ServiceOutageProvider>
            <ServiceOutageBanner />
          </ServiceOutageProvider>
          <AuthGate>
          <NotificationsProvider>
          <PullToRefresh>
          {/* RMAWizardProvider mounts a single instance of the New-Case
              wizard modal. Any component can open it via
              useRMAWizard().openWizard(). The /rma/new route shim also
              uses this to open the modal on deep-link hit. */}
          <RMAWizardProvider>
          {/* 🔴 ZWEI Boundaries, und sie sind nicht redundant. Die aeussere
              faengt die zwei Routen ausserhalb des Layouts (`vendor-reports/...`)
              und einen Renderfehler des Layouts selbst; suspendieren kann das
              Layout nicht (eager, Daten per axios). Daher `homeHref={null}`: `/`
              schickte Lieferanten in den Login bzw. wiederholte den Absturz.
              Die innere liegt um den Outlet in `SidebarAndHeaderBar`. */}
          {/* 🔴 ErrorBoundary AUSSEN, Suspense innen. Ohne die Boundary
              unmountet React bei einem fehlgeschlagenen Chunk den ganzen Baum —
              weisse Seite, nicht ein sichtbarer Fehler. Der Service Worker
              mildert das, erholt sich aber nicht davon. */}
          <AeussereGrenze>
          <Suspense fallback={<RouteFallback />}>
          <Routes>
            <Route path="/" element={<SidebarAndHeaderBar onCreateStandalone={openWizard} onCreateVariant={openVariantWizard}/>}>
              <Route
                index
                element={
                  <ProtectedRoute>
                    <HomeDashboard />
                  </ProtectedRoute>
                }
              />
              {/* Authed users hitting /login → home. Unauthed users never reach
                  this route — AuthGate intercepts before Routes mount. */}
              <Route path="login" element={<Navigate to="/" replace />} />
              <Route path="overview" element={<ProtectedRoute><POOverview /></ProtectedRoute>} />
              <Route path="new" element={<ProtectedRoute><PODetail mode="create" /></ProtectedRoute>} />
              <Route path="purchase_order/:po_id" element={<ProtectedRoute><PODetail /></ProtectedRoute>} />
              <Route path="po-inbox" element={<ProtectedRoute><POInbox /></ProtectedRoute>} />
              <Route path="products" element={<ProtectedRoute><ProductsOverview /></ProtectedRoute>} />
              <Route path="products/attributes" element={<ProtectedRoute><ProductsAttributesPage /></ProtectedRoute>} />
              {/* 🔴 `products/completion` steht VOR `products/:sku` — die
                  Reihenfolge entscheidet hier nichts (React Router 7 ordnet ein
                  statisches Segment ueber ein dynamisches, unabhaengig von der
                  Zeile), aber wer die Datei liest, soll die Kollision sehen.
                  Der Preis: eine SKU mit dem exakten String „completion" waere
                  ueber ihre PDP nicht mehr erreichbar. SKUs sind hier
                  Ziffernfolgen; der Preis ist benannt, nicht uebersehen. */}
              <Route path="products/completion" element={<ProtectedRoute><ProductCompletionOverview /></ProtectedRoute>} />
              <Route path="products/:sku" element={<ProtectedRoute><ProductDetail /></ProtectedRoute>} />
              <Route path="products/:sku/markets/:marketId" element={<ProtectedRoute><ProductDetail /></ProtectedRoute>} />
              <Route path="products/:sku/taxonomy" element={<ProtectedRoute><ProductTaxonomyDetail /></ProtectedRoute>} />
              <Route path="second-life" element={<ProtectedRoute><SecondLifeBestand /></ProtectedRoute>} />
              <Route path="recommendations" element={<ProtectedRoute><RecommendationsPage /></ProtectedRoute>} />
              <Route path="relations-graph" element={<RelationsGraphRedirect />} />
              <Route path="incoming-inventory/:view?" element={<ProtectedRoute><IncomingInventory /></ProtectedRoute>} />
              <Route path="kn-shipments" element={<ProtectedRoute><KnShipments /></ProtectedRoute>} />
              <Route path="kn-shipments/:id" element={<ProtectedRoute><KnShipmentDetail /></ProtectedRoute>} />
              <Route path="fulfillment" element={<ProtectedRoute><FulfillmentDashboard /></ProtectedRoute>} />
              <Route path="vendors" element={<ProtectedRoute><VendorsOverview /></ProtectedRoute>} />
              {/* 🔴 W-15 — diese beiden hingen als EINZIGE der Nachbarschaft ohne
                  `ProtectedRoute` da: `vendors`, `vendors/reports`, `fca` daneben
                  alle geschuetzt. Vorbestehend. `AuthGate` faengt Unangemeldete
                  schon vorher ab — das hier ist die zweite Schicht wie bei jedem
                  Geschwister, nicht die einzige. */}
              <Route path="vendors/:id" element={<ProtectedRoute><VendorDetails asPage /></ProtectedRoute>} />
              <Route path="vendors/new" element={<ProtectedRoute><VendorWizard /></ProtectedRoute>} />
              <Route path="vendors/reports" element={<ProtectedRoute><VendorReports /></ProtectedRoute>} />
              <Route path="fca" element={<ProtectedRoute><FCADashboard /></ProtectedRoute>} />
              {/* 🔴 Altpfade aus BEREITS VERSENDETEN Benachrichtigungen.
                  Sechs `deep_link`-Ziele im Backend zeigten bis zum 2026-09-19 auf
                  Routen, die es nie gab; sie fielen auf <Route path="*"> und zeigten
                  NotFound. Die Zeichenketten sind korrigiert — aber jede Zeile, die
                  schon in `notifications` liegt, traegt den alten Pfad weiter, und
                  `NotificationCenter` navigiert direkt darauf. Versendete
                  Morgen-Mails tragen ihn als absolute URL und sind gar nicht mehr
                  zu erreichen.
                  Darum zusaetzlich Weiterleitungen. NEUER Code darf sie nicht als
                  Ziel nehmen: `tests/infra/test_deep_links_treffen_echte_routen.py`
                  weist `<Navigate>`-Attrappen als deep_link-Ziel ausdruecklich ab.

                  Dass es BENANNTE Weiterleitungen sind und kein Sammelfang, ist
                  der Unterschied zu dem, was `NotFound.jsx` bewusst abgeschafft hat:
                  dort ging JEDER unbekannte Pfad still auf `/overview`, und kein
                  kaputter Link wurde je sichtbar. Hier faengt `<Route path="*">`
                  weiterhin alles Uebrige ab, und das Abdriften meldet ab jetzt die CI,
                  bevor eine Meldung ueberhaupt verschickt wird.

                  WANN DIESER BLOCK WEG DARF: wenn keine `notifications`-Zeile mehr
                  einen dieser Pfade traegt. Eine Aufraeumung fuer `notifications` gibt
                  es derzeit NICHT (nur fuer `notification_email_log`,
                  `backend/notify/cron_email_log_purge.py`) -- also bis auf Weiteres
                  nicht. Vor dem Loeschen nachzaehlen, nicht schaetzen. */}
              <Route path="recommended-orders" element={<ProtectedRoute><Navigate to="/overview?section=recommended" replace /></ProtectedRoute>} />
              <Route path="purchase-orders" element={<ProtectedRoute><Navigate to="/po-inbox" replace /></ProtectedRoute>} />
              <Route path="pricing" element={<ProtectedRoute><Navigate to="/" replace /></ProtectedRoute>} />
              {/* `/home` — W-18 hat die Route geloescht (sie rendert wortgleich
                  dasselbe wie die Index-Route `/`). `backend/admin_scheduler.py`
                  schickte sie aber bis W-16 als `deep_link` der Preis-Warteschlange
                  und der Automatik-Zusammenfassung; jede Zeile, die schon in
                  `notifications` liegt, traegt den Pfad weiter. Ziel ist die
                  Index-Route, die `HomeDashboard` rendert. */}
              <Route path="home" element={<ProtectedRoute><Navigate to="/" replace /></ProtectedRoute>} />
              <Route path="market-lab" element={<ProtectedRoute><Navigate to="/intelligence/market-lab" replace /></ProtectedRoute>} />
              <Route path="market" element={<ProtectedRoute><Navigate to="/intelligence/market-lab" replace /></ProtectedRoute>} />
              <Route path="admin/sync-status" element={<ProtectedRoute><Navigate to="/admin/jobs" replace /></ProtectedRoute>} />
              <Route path="/pricing-overview" element={<ProtectedRoute><Navigate to="/products" replace /></ProtectedRoute>} />
              {/* 🔴 Altpfade aus dem UX-Land-Rueckbau (PR 1, Owner-Entscheide
                  W19/B1/C4/C5 in `docs/forge-ux-redesign/ENTSCHEIDE.md`). Vier
                  Routen sind ersatzlos entfernt worden; wo es eine Nachfolgeflaeche
                  GIBT, leitet eine BENANNTE Weiterleitung dorthin — kein
                  Sammelfang, aus demselben Grund wie im Block darueber:
                    admin/legacy/:section?        -> /admin              (W19/B1: die
                        v2-Adminflaeche unter AdminLayout ersetzt AdminConfigPage)
                    admin/cron-healthboard        -> /admin/cron-failures (C4:
                        „cron-failures deckt es ab")
                    admin/knowledge/taxonomy-guide-> /taxonomy           (C5/B1: der
                        Leitfaden lebt als Tab unter /taxonomy weiter)
                    competitors/:compId/edit      -> competitors/:compId (die alte
                        Zeile renderte bereits CompetitorDetail)
                  OHNE Nachfolgeflaeche und deshalb BEWUSST 404 — KEIN `<Navigate>`:
                    canonical-type-generator      (C5: Komponente geloescht)
                    rma/vendor-configs            (C5: Komponente geloescht)
                  Ein Lesezeichen auf die zwei letzten landet auf `NotFound.jsx`.
                  Das ist gewollt: es gibt keine Flaeche, auf die man zeigen koennte,
                  und ein stiller Umweg waere genau das, was `NotFound.jsx`
                  abgeschafft hat. */}
              <Route path="admin/legacy/:section?" element={<ProtectedRoute><Navigate to="/admin" replace /></ProtectedRoute>} />
              <Route path="admin/cron-healthboard" element={<ProtectedRoute><Navigate to="/admin/cron-failures" replace /></ProtectedRoute>} />
              <Route path="admin/knowledge/taxonomy-guide" element={<ProtectedRoute><Navigate to="/taxonomy" replace /></ProtectedRoute>} />
              <Route path="competitors/:compId/edit" element={<ProtectedRoute><CompetitorEditRedirect /></ProtectedRoute>} />
              {/* Meldungsregister: der Pfad heisst seit dem 2026-10-19 `/declarations`.
                  Der fruehere Pfad `meldungen` war deutsch, waehrend die Flaeche selbst englisch ist
                  (Owner-Befund 2026-10-18). Die Detailweiterleitung nimmt die `id` mit. */}
              <Route path="meldungen" element={<ProtectedRoute><Navigate to="/declarations" replace /></ProtectedRoute>} />
              <Route path="meldungen/:id" element={<ProtectedRoute><DeclarationDetailRedirect /></ProtectedRoute>} />
              <Route path="scrapers" element={<ProtectedRoute><ScraperConfigList /></ProtectedRoute>} />
              <Route path="scrapers/new" element={<ProtectedRoute><ScraperConfigForm /></ProtectedRoute>} />
              <Route path="scrapers/:configId/edit" element={<ProtectedRoute><ScraperConfigForm /></ProtectedRoute>} />
              <Route path="scrapers/redirect-reviews" element={<ProtectedRoute><RedirectReviewInbox /></ProtectedRoute>} />
              {/* E5a-2 — die Liste des naechtlichen Preis-Pruefers. Ohne sie
                  waere `scr_watch_finding` ein Signal ohne Leser. */}
              <Route path="scrapers/watch-findings" element={<ProtectedRoute><WatchFindings /></ProtectedRoute>} />
              <Route path="intelligence/market-lab" element={<ProtectedRoute><MarketLab /></ProtectedRoute>} />
              <Route path="competitors"element={<ProtectedRoute><CompetitorList /></ProtectedRoute>}/>
              {/* 🔴 Diese beiden hingen als EINZIGE der Nachbarschaft ohne
                  `ProtectedRoute` da — `competitors`, `market-lab` und `taxonomy`
                  daneben alle geschuetzt. Vorbestehend, aber E5b schickt jetzt
                  Verkehr dorthin („Wettbewerber oeffnen"), also nachgezogen.
                  Vom Regel-16-Gate gefunden. */}
              <Route path="competitors/:compId" element={<ProtectedRoute><CompetitorDetail /></ProtectedRoute>} />
              <Route path="taxonomy" element={<ProtectedRoute><TaxonomyHub /></ProtectedRoute>} />
              {/* New consolidated admin mini-app — Phase 1 (2026-04-30).
                  See docs/superpowers/specs/2026-04-30-admin-ui-rewrite-design.md.
                  Sections 2-5 stub to <ComingSoon /> until their phase ships. */}
              <Route path="admin" element={<ProtectedRoute><AdminLayout /></ProtectedRoute>}>
                <Route index element={<AdminDashboard />} />
                <Route path="integrations" element={<IntegrationsList />} />
                <Route path="jobs" element={<JobsList />} />
                <Route path="jobs/:key" element={<JobDetail />} />
                <Route path="audit" element={<AuditFeed />} />
                <Route path="secrets" element={<SecretsList />} />
                <Route path="notifications" element={<NotificationTriggers />} />
                <Route path="cron-failures" element={<CronFailures />} />
                <Route path="docs" element={<DocsIndex />} />
                <Route path="docs/:topic" element={<DocViewer />} />
                <Route path="settings" element={<AdminSettings />} />
                {/* Die fuenf Werkzeugflaechen des Admin-Menues (SECTIONS,
                    `secondary: true`) — bis zum 2026-10-06 standen die vier
                    hier drunter als eigenstaendige Routen und rendeten ohne
                    Tab-Leiste und ohne Status-Pill. */}
                <Route path="email-log" element={<EmailLog />} />
                <Route path="notification-tester" element={<NotificationTester />} />
                <Route path="spare-parts-review" element={<SparePartReviewQueue />} />
                <Route path="privacy" element={<PrivacyErasePanel />} />
              </Route>
              {/* `/blog-sources` — der Quellen-Manager des Blogs. Stand bis zum
                  UX-Land-Rueckbau (PR 1) in der „Legacy section-routed admin"-
                  Gruppe; die Gruppe ist entfallen (W19/C5), dieser Eintrag bleibt,
                  weil er eine EIGENE Flaeche rendert und kein Altpfad ist. */}
              <Route path="blog-sources" element={<ProtectedRoute><SourceManagerPage /></ProtectedRoute>} />
              {/* Der Komponentenkatalog (`/design`). Kein dauerhafter
                  Seitenleisten-Eintrag — erreichbar ueber die Doku-Uebersicht
                  (`/admin/docs`), weil das eine seltene Werkzeugflaeche ist
                  (pattern-library §Owner-Grundregel). */}
              <Route path="design" element={<ProtectedRoute><DesignCatalog /></ProtectedRoute>} />
              {/* Die Musterroute (UI2 Teil 10, G0.11) — dieselbe Hausform wie
                  `/design`: kein Seitenleisten-Eintrag, `lazy()`-geladen, und in
                  einem Produktionsbau liefert `musterrouteEintrag()` `null`, es
                  gibt die Route dort also nicht. */}
              {musterrouteEintrag()}
              <Route path="ops/dep-health" element={<ProtectedRoute><DepHealth /></ProtectedRoute>} />
              <Route path="ops/lsm-watcher" element={<ProtectedRoute><LsmWatcher /></ProtectedRoute>} />
              <Route path="ops/po-anomalies" element={<ProtectedRoute><PoAnomalies /></ProtectedRoute>} />
              {/* Cases-overview shell at /rma. Specific subpaths
                  (/rma/inbox, /rma/new, /rma/reports, /rma/:id) are matched
                  FIRST since they're declared first. */}
              <Route path="rma/inbox" element={<ProtectedRoute><RMAInbox /></ProtectedRoute>} />
              <Route path="rma/new" element={<ProtectedRoute><RMANewCaseWizard /></ProtectedRoute>} />
              <Route path="rma/reports" element={<ProtectedRoute><RMAReports /></ProtectedRoute>} />
              <Route path="rma/sla-overview" element={<ProtectedRoute><RMASlaOverview /></ProtectedRoute>} />
              <Route path="rma/faelligkeit" element={<ProtectedRoute><RMAFaelligkeit /></ProtectedRoute>} />
              <Route path="rma/analytics/inquiry-products" element={<ProtectedRoute><ProductInquiryAnalytics /></ProtectedRoute>} />
              <Route path="reports/refunds" element={<ProtectedRoute><RefundsReport /></ProtectedRoute>} />
              {/* Kundenrechnungen: Liste (S5.4-F, Reiter S5.7-F) und Detail (S5.5-F). */}
              <Route path="invoicing" element={<ProtectedRoute><InvoiceList /></ProtectedRoute>} />
              <Route path="invoicing/:id" element={<ProtectedRoute><InvoiceDetail /></ProtectedRoute>} /><Route path="monatsabschluss" element={<ProtectedRoute><Monatsabschluss /></ProtectedRoute>} />
              {/* Meldungsregister: das Register und eine Meldung. */}
              <Route path="declarations" element={<ProtectedRoute><Meldungen /></ProtectedRoute>} />
              <Route path="declarations/:id" element={<ProtectedRoute><MeldungDetail /></ProtectedRoute>} />
              {/* Stufe K (2026-10-16): die Kundenseite. NUR LESEND; der Weg
                  hierher ist der Fallkopf (Stufe K, Grill B3) und spaeter der
                  Suchtreffer (Stufe N1). */}
              <Route path="customers/:id" element={<ProtectedRoute><CustomerDetail /></ProtectedRoute>} />
              <Route path="rma/inquiry-knowledge" element={<ProtectedRoute><InquiryKnowledgeAdmin /></ProtectedRoute>} />
              {/* Tombstone for the deleted Kanban surface — see BoardRedirect. */}
              <Route path="rma/board" element={<ProtectedRoute><BoardRedirect /></ProtectedRoute>} />
              <Route path="rma" element={<ProtectedRoute><RMAOverviewLayout /></ProtectedRoute>} />
              {/* 🔴 ZURUECKGEDREHT (W4, 2026-10-23). Der Owner, woertlich:
                  *"Ja, Classic als standard wieder setzen - und pruefen, warum
                  der Standard view mobil in crashed!"* Also ist `rma/:id` wieder
                  die ALTE Ansicht (`rma/RMADetail`), `rma/:id/neu` die NEUE
                  (`fall/FallAnsicht`), und `rma/:id/classic` bleibt als
                  ausdruecklicher Rueckfall-Link gueltig (Lesezeichen und der
                  Menuepunkt „Open classic view" der neuen Ansicht bauen ihn).
                  Bis zum 2026-10-23 war es umgekehrt (S1-8f, Owner-Entscheid
                  42): `rma/:id` neu, `/classic` alt, `/neu` leitete um.
                  Die alten `rma/*`-Statiken (inbox, new, reports, ...) stehen
                  unveraendert DARUEBER und gewinnen per Rangfolge; react-router
                  rankt ohnehin nach Spezifitaet, nicht nach Reihenfolge. */}
              <Route path="rma/:id/classic" element={<ProtectedRoute><RMADetail /></ProtectedRoute>} />
              <Route path="rma/:id/neu" element={<ProtectedRoute><FallAnsicht /></ProtectedRoute>} />
              <Route path="rma/:id" element={<ProtectedRoute><RMADetail /></ProtectedRoute>} />
              <Route path="discovery" element={<ProtectedRoute><DiscoveryInbox /></ProtectedRoute>} />
              <Route path="media" element={<ProtectedRoute><MediaLibraryPage /></ProtectedRoute>} />
              <Route path="notifications" element={<ProtectedRoute><NotificationCenter /></ProtectedRoute>} />
              <Route path="settings/notifications" element={<ProtectedRoute><SettingsNotifications /></ProtectedRoute>} />
              <Route path="admin/gdpr-wipe" element={<ProtectedRoute><GDPRWipePanel /></ProtectedRoute>} />
              <Route path="*" element={<ProtectedRoute><NotFound /></ProtectedRoute>} />
            </Route>
            <Route path="vendor-reports/public/:reportId" element={<VendorReportView />} />
            <Route path="vendor-reports/view/:reportId" element={<ProtectedRoute><VendorReportView /></ProtectedRoute>} />
          </Routes>
          </Suspense>
          </AeussereGrenze>
          </RMAWizardProvider>
          </PullToRefresh>
          </NotificationsProvider>
          </AuthGate>
          {productWizard.mounted && (
            <NewProductWizard open={productWizard.open} onClose={closeWizard} />
          )}
          {variantWizard.mounted && (
            <UnifiedCreateWizard
              open={variantWizard.open}
              mode="variants"
              onClose={closeVariantWizard}
              onSuccess={() => {
                setVariantWizardOpen(false);
                closeVariantWizard();
              }}
            />
          )}
        </Router>      
      {/*</LoadingProvider> */}
    </ThemeProvider>
    </ColorModeContext.Provider>
    </MotionConfig>
  );
}

export default App;

/**
 * Die aeussere ErrorBoundary, mit `resetKey` am Ort. `App` rendert den Router
 * selbst und kann `useLocation()` nicht rufen; dieses Kind innerhalb schon.
 * Ohne den Schluessel bliebe die Karte nach einem Absturz stehen, auch wenn die
 * Zurueck-Taste eine intakte Seite laedt (Regel-16-Wiedervorlage 3, Bahn B, W1).
 */
function AeussereGrenze({ children }) {
  const { pathname } = useLocation();
  return <ErrorBoundary homeHref={null} resetKey={pathname}>{children}</ErrorBoundary>;
}
